import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "../../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { UserCircle, Target, ClipboardText, TrendUp, ArrowSquareOut, ChatCircleText, Sparkle, CircleNotch } from "@phosphor-icons/react";
import serviceMeta from "../../data/serviceMeta.json";

const STAGE_ORDER = ["lead", "engagement", "organizer", "waiting_docs", "preparation", "cpa_review", "client_review", "e8879", "ready_efile", "accepted", "archived"];
const STAGE_LABEL = {
  lead: "Lead", engagement: "Engaged", organizer: "Organizer sent", waiting_docs: "Awaiting documents",
  preparation: "In preparation", cpa_review: "CPA review", client_review: "Client review",
  e8879: "Awaiting e-signature", ready_efile: "Ready to e-file", accepted: "Accepted", archived: "Filed",
};

const CONFIDENCE_BADGE = {
  high: "bg-emerald-50 text-emerald-800 border-emerald-200",
  medium: "bg-amber-50 text-amber-800 border-amber-200",
  low: "bg-zinc-50 text-zinc-700 border-zinc-200",
};

export function ServiceDrawer({ service, engagement, cpa, onOpenChange, onMessageFirm }) {
  const open = !!service;
  const meta = service?.slug ? serviceMeta.services?.[service.slug] : null;

  // Smart Service Recommender — hit LLM once per drawer open, cached 24h server-side
  const { data: recData, isLoading: recLoading } = useQuery({
    queryKey: ["service-recs"],
    queryFn: () => api.post("/portal/recommend-services").then((r) => r.data),
    enabled: open,
    staleTime: 1000 * 60 * 10, // 10 min client cache
  });
  const smartRecs = (recData?.recommendations || []).filter((r) => r.slug !== service?.slug);
  // Fallback to the static related list only if the LLM returned nothing
  const staticRelated = (meta?.related || []).filter((s) => s !== service?.slug).slice(0, 3);
  const recommendationsList = smartRecs.length > 0
    ? smartRecs
    : staticRelated.map((slug) => ({ slug, reason: `Frequently paired with ${meta?.title || "your service"}.`, confidence: "medium" }));

  const stageIdx = engagement ? Math.max(0, STAGE_ORDER.indexOf(engagement.stage)) : 0;
  const progressPct = Math.round((stageIdx / (STAGE_ORDER.length - 2)) * 100); // exclude "archived"

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onOpenChange(null)}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="service-drawer">
        <SheetHeader>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-100 border border-zinc-200 rounded-sm px-1.5 py-0.5">
              {meta?.eyebrow || service?.service_type}
            </span>
            {meta?.category && (
              <span className="text-[10px] uppercase tracking-wider text-zinc-500">· {meta.category}</span>
            )}
          </div>
          <SheetTitle className="font-heading text-lg leading-tight" data-testid="service-drawer-title">
            {service?.title || meta?.title || "Service"}
          </SheetTitle>
          {meta?.pillDesc && <p className="text-xs text-zinc-500 mt-1">{meta.pillDesc}</p>}
        </SheetHeader>

        <div className="mt-5 space-y-5">
          {/* Assigned CPA */}
          <section className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-4" data-testid="drawer-assigned-cpa">
            <div className="flex items-center gap-2 mb-1.5">
              <UserCircle size={14} weight="fill" className="text-zinc-500" />
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Assigned CPA</p>
            </div>
            <p className="text-sm font-medium text-zinc-900">{cpa?.name || "Partner (auto-routed)"}</p>
            <p className="text-[11px] text-zinc-500">{cpa?.email || "cpa@arthataxes.com"} · Frisco, TX</p>
          </section>

          {/* Scope */}
          {meta?.seoDescription && (
            <section data-testid="drawer-scope">
              <div className="flex items-center gap-2 mb-1.5">
                <Target size={14} weight="fill" className="text-zinc-500" />
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">Scope</p>
              </div>
              <p className="text-xs text-zinc-700 leading-relaxed">{meta.seoDescription}</p>
            </section>
          )}

          {/* Progress */}
          <section data-testid="drawer-progress">
            <div className="flex items-center gap-2 mb-1.5">
              <TrendUp size={14} weight="fill" className="text-zinc-500" />
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Progress</p>
            </div>
            {engagement ? (
              <>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-medium text-zinc-800">{STAGE_LABEL[engagement.stage] || engagement.stage}</span>
                  <span className="text-[10px] text-zinc-500">{progressPct}%</span>
                </div>
                <Progress value={progressPct} className="h-1.5" />
                <p className="text-[10px] text-zinc-400 mt-1.5">Tax year {engagement.tax_year} · Form {engagement.service_type}</p>
              </>
            ) : (
              <p className="text-xs text-zinc-500">No active engagement — reach out via <span className="text-zinc-800 font-medium">Message my CPA</span> below to start one.</p>
            )}
          </section>

          {/* Next Steps */}
          <section data-testid="drawer-next-steps">
            <div className="flex items-center gap-2 mb-1.5">
              <ClipboardText size={14} weight="fill" className="text-zinc-500" />
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Next steps</p>
            </div>
            <ul className="text-xs text-zinc-700 space-y-1.5">
              <li className="flex gap-2"><span className="text-zinc-400">·</span> Complete outstanding checklist items in the Documents tab.</li>
              <li className="flex gap-2"><span className="text-zinc-400">·</span> Message your CPA with clarifications directly from this panel.</li>
              <li className="flex gap-2"><span className="text-zinc-400">·</span> Sign engagement letters &amp; Form 8879 when ready.</li>
            </ul>
          </section>

          {/* Upsell / related — Smart Service Recommender (LLM-powered) with static fallback */}
          {(recLoading || recommendationsList.length > 0) && (
            <section data-testid="drawer-upsell">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <Sparkle size={14} weight="fill" className="text-emerald-600" />
                  <p className="text-[10px] uppercase tracking-wider text-emerald-700">
                    {smartRecs.length > 0 ? "AI-recommended for you" : "Recommended for you"}
                  </p>
                </div>
                {recLoading && <CircleNotch size={11} className="animate-spin text-emerald-600" />}
              </div>
              <div className="space-y-1.5">
                {recommendationsList.map((rec) => {
                  const r = serviceMeta.services?.[rec.slug];
                  if (!r) return null;
                  return (
                    <Link
                      key={rec.slug}
                      to={`/services/${rec.slug}`}
                      data-testid={`drawer-upsell-${rec.slug}`}
                      className="block rounded-md border border-zinc-200 hover:border-emerald-500 hover:bg-emerald-50/40 bg-white px-2.5 py-2 transition-colors duration-150">
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <p className="text-xs font-medium text-slate-900 truncate">{r.title}</p>
                        <div className="flex items-center gap-1 shrink-0">
                          {smartRecs.length > 0 && (
                            <span className={`text-[9px] uppercase tracking-wider rounded-sm border px-1 py-0.5 ${CONFIDENCE_BADGE[rec.confidence] || CONFIDENCE_BADGE.medium}`}>
                              {rec.confidence}
                            </span>
                          )}
                          <ArrowSquareOut size={11} className="text-zinc-400" />
                        </div>
                      </div>
                      <p className="text-[10px] text-zinc-500 leading-snug">{rec.reason || r.pillDesc}</p>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-2 pt-2">
            <Button data-testid="drawer-message-firm-btn" size="sm" onClick={() => { onMessageFirm?.(service); onOpenChange(null); }}>
              <ChatCircleText size={13} className="mr-1.5" /> Message my CPA about this
            </Button>
            {service?.slug && (
              <Link
                to={`/services/${service.slug}`}
                data-testid="drawer-view-details-btn"
                onClick={() => onOpenChange(null)}
                className="flex items-center justify-center gap-1.5 text-xs text-zinc-700 border border-zinc-200 rounded-md py-1.5 hover:bg-zinc-50 transition-colors duration-150">
                <ArrowSquareOut size={12} /> View full service details
              </Link>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
